import React, { useState, useEffect, useRef } from "react";
// Use app-wide history to ensure navigation works even outside hooks
import { history as appHistory } from "../../utils/navigation";
import Joyride, { EVENTS, STATUS, ACTIONS } from "react-joyride";
import "./productTour.css";
import { COLORS } from "../../constants/colors";
import { useDispatch } from "react-redux";
import { fetchGettingStarted } from "../../actions/gettingStarted";
import ProductTourItem from "./productTourItem";
import ErrorBoundary from "../errorBoundary/errorBoundary";
import {
  updateCompletedStories,
  updateCompletedStoriesSuccess,
} from "../../authentication/actions";

const stepData = {
  disableBeacon: true,
  disableOverlayClose: true,
  hideCloseButton: false,
  placement: "bottom",
  styles: {
    options: {
      zIndex: 10000,
    },
  },
};

const spotlightClass =
  "#react-joyride-portal > .react-joyride__overlay > .react-joyride__spotlight";

// Story UUIDs are compared case-insensitively: migrated (Drupal) completions
// come back from the backend uppercase, while gettingStartedStories.json
// defines story uuids lowercase.
const isStoryCompleted = (completedStories, storyUuid) =>
  !!storyUuid &&
  Array.isArray(completedStories) &&
  completedStories.some((id) => id?.toLowerCase() === storyUuid.toLowerCase());

const ProductTour = ({ isOpen, stories, completedStories, isLoggedIn }) => {
  const history = appHistory;
  const dispatch = useDispatch();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedStory, setSelectedStory] = useState(null);
  const [steps, setSteps] = useState(null);
  const [spotLightEvent, setSpotLightEvent] = useState(false);
  const [currentStepReady, setCurrentStepReady] = useState(false);
  const [tourKey, setTourKey] = useState(0); // Force remount Joyride when needed
  const [isWaitingForTarget, setIsWaitingForTarget] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetries] = useState(5);
  const waitingTimeoutRef = useRef(null);
  // Tracks story UUIDs already dispatched for completion, so the completion
  // effect doesn't re-fire while waiting for the async backend response
  // (the effect also re-runs whenever `stories` churns from
  // window.updateGettingStartedStories, before `completedStories` updates).
  const dispatchedCompletionsRef = useRef(new Set());
  const [expandedStoryId, setExpandedStoryId] = useState(null);

  // Check if current step target exists in DOM
  useEffect(() => {
    if (!steps || !steps[stepIndex]) {
      setCurrentStepReady(false);
      return;
    }

    const currentStep = steps[stepIndex];
    if (!currentStep.target) {
      setCurrentStepReady(true);
      // Clear waiting state if there's no target to wait for
      setIsWaitingForTarget(false);
      return;
    }

    // Check if target exists in DOM
    const targetExists = document.querySelector(currentStep.target);
    setCurrentStepReady(!!targetExists);

    // If target exists and we were waiting, clear the waiting state and force Joyride to continue
    if (targetExists && isWaitingForTarget) {
      // Clear the timeout since we found the element
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
      setIsWaitingForTarget(false);
      setRetryCount(0);
      // Force Joyride to retry by toggling run state
      setRun(false);
      setTimeout(() => {
        setRun(true);
      }, 1000);
    }

    // If target doesn't exist, poll for it
    if (!targetExists && currentStep.target) {
      const pollInterval = setInterval(() => {
        const element = document.querySelector(currentStep.target);
        if (element) {
          setCurrentStepReady(true);
          clearInterval(pollInterval);
          // Clear waiting state when element is found and force Joyride to continue
          if (isWaitingForTarget) {
            // Clear the timeout since we found the element
            if (waitingTimeoutRef.current) {
              clearTimeout(waitingTimeoutRef.current);
              waitingTimeoutRef.current = null;
            }
            setIsWaitingForTarget(false);
            setRetryCount(0);
            // Force Joyride to retry by toggling run state
            setRun(false);
            setTimeout(() => {
              setRun(true);
            }, 100);
          }
        }
      }, 100);

      // Clear polling after 10 seconds (match the timeout duration)
      setTimeout(() => {
        clearInterval(pollInterval);
      }, 10000);

      return () => clearInterval(pollInterval);
    }
  }, [steps, stepIndex, tourKey, isWaitingForTarget, retryCount]); // Added all deps to properly clear waiting state

  useEffect(() => {
    if (isOpen === true) {
      if (selectedStory && (currentStepReady || isWaitingForTarget)) {
        // Start when current step is ready OR when waiting for target
        setRun(true);
      } else {
        setRun(false);
      }
    } else {
      // When tour is closed, reset everything
      resetTourState();
    }
  }, [isOpen, selectedStory, currentStepReady, isWaitingForTarget]);

  const changeStepIndex = (param) => {
    setStepIndex(param);
    setSpotLightEvent(false);
  };

  // Complete reset of tour state
  const resetTourState = () => {
    // Clear any pending timeout
    if (waitingTimeoutRef.current) {
      clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = null;
    }
    setRun(false);
    setStepIndex(0);
    setSelectedStory(null);
    setSteps(null);
    setCurrentStepReady(false);
    setSpotLightEvent(false);
    setIsWaitingForTarget(false);
    setRetryCount(0);
    // Increment key to force Joyride remount on next open
    setTourKey((prev) => prev + 1);
  };

  useEffect(() => {
    dispatch(fetchGettingStarted(isLoggedIn));
  }, [isLoggedIn]);

  useEffect(() => {
    const handleSpotlightEvents = (event) => {
      if (selectedStory && steps && steps[stepIndex]) {
        try {
          const target = steps[stepIndex].target;
          if (target && event.target.closest(target)) {
            const isLastStep = stepIndex === steps.length - 1;

            // If this is the last step with spotlight click, mark as completed
            if (isLastStep) {
              const isCompletedStory =
                isStoryCompleted(completedStories, selectedStory) ||
                dispatchedCompletionsRef.current.has(selectedStory);

              if (!isCompletedStory) {
                dispatchedCompletionsRef.current.add(selectedStory);

                // Update the story's completedCondition to "FINISHED"
                const updatedStories = stories.map((story) => {
                  if (story.uuid === selectedStory) {
                    return { ...story, completedCondition: "FINISHED" };
                  }
                  return story;
                });

                // Save updated stories to localStorage for persistence
                localStorage.setItem(
                  "gettingStartedStoriesState",
                  JSON.stringify(updatedStories)
                );

                // Dispatch action to update stories in the parent component
                if (window.updateGettingStartedStories) {
                  window.updateGettingStartedStories(updatedStories);
                }

                if (isLoggedIn) {
                  dispatch(updateCompletedStories(selectedStory));
                } else {
                  dispatch(
                    updateCompletedStoriesSuccess([
                      ...completedStories,
                      selectedStory,
                    ])
                  );
                }
              }

              // Reset tour after completing last step via spotlight
              setTimeout(() => {
                resetTourState();
              }, 500); // Small delay to show completion
            } else {
              // Not the last step, advance normally
              changeStepIndex(stepIndex + 1);
              setSpotLightEvent(true);
            }
          }
        } catch (error) {
          console.warn("ProductTour: handleSpotlightEvents error", error);
        }
      }
    };

    document.addEventListener("click", handleSpotlightEvents, true);

    const isCompletedStory =
      isStoryCompleted(completedStories, selectedStory) ||
      dispatchedCompletionsRef.current.has(selectedStory);

    // Mark as completed when ON the last step (not after it)
    if (
      selectedStory &&
      steps &&
      stepIndex === steps.length - 1 &&
      !isCompletedStory
    ) {
      dispatchedCompletionsRef.current.add(selectedStory);

      // Update the story's completedCondition to "FINISHED"
      const updatedStories = stories.map((story) => {
        if (story.uuid === selectedStory) {
          return { ...story, completedCondition: "FINISHED" };
        }
        return story;
      });

      // Save updated stories to localStorage for persistence
      localStorage.setItem(
        "gettingStartedStoriesState",
        JSON.stringify(updatedStories)
      );

      // Dispatch action to update stories in the parent component
      if (window.updateGettingStartedStories) {
        window.updateGettingStartedStories(updatedStories);
      }

      if (isLoggedIn) {
        dispatch(updateCompletedStories(selectedStory));
      } else {
        dispatch(
          updateCompletedStoriesSuccess([...completedStories, selectedStory])
        );
      }
    }
    return () => {
      document.removeEventListener("click", handleSpotlightEvents);
    };
  }, [
    stepIndex,
    completedStories,
    stories,
    selectedStory,
    steps,
    dispatch,
    isLoggedIn,
  ]);

  // Navigate to the current step's navigationUrl (if any) when step changes
  useEffect(() => {
    if (!steps || !steps.length) return;
    const s = steps[stepIndex];
    if (!s || !s.navigationUrl) return;
    const url = s.navigationUrl;
    const isAbsolute = /^https?:\/\//i.test(url);

    if (isAbsolute) {
      window.location.href = url;
    } else {
      history.push(url);
    }
  }, [steps, stepIndex]);

  // (no-op)

  const calculateSpotLightTop = (target) => {
    try {
      const targetElement = document.querySelector(target);
      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      if (!rect) return;

      const currentSpotlight = document.querySelector(spotlightClass);
      if (currentSpotlight) {
        currentSpotlight.style.top = `${rect.top - 35}px`;
      }
    } catch (error) {
      console.warn("ProductTour: calculateSpotLightTop error", error);
    }
  };

  const handleProductTourStyling = (styles) => {
    // Convert payload from api call from text value with new lines and breaks -> array of values
    let styleValues = styles
      .replace(/(\r\n|\n|\r)/gm, " ")
      .trimEnd()
      .split(" ");

    // create an Object with styles as { top: "50px" }
    let stylesObject = styleValues.reduce((acc, val, index) => {
      if (index % 2 === 0) acc[val] = styleValues[index + 1];
      return acc;
    }, {});

    const currentSpotlight = document.querySelector(spotlightClass);

    if (currentSpotlight) {
      // Iterate on styles object
      stylesObject &&
        Object.keys(stylesObject).map(
          (s) => (currentSpotlight.style[s] = stylesObject[s])
        );
    }
  };

  const handleJoyrideCallback = (data) => {
    const { type, action, step, index, status, lifecycle } = data;

    // Clear waiting state only on successful tooltip render (means target was found)
    if (type === EVENTS.TOOLTIP) {
      if (isWaitingForTarget) {
        setIsWaitingForTarget(false);
        setRetryCount(0);
      }
    }

    // Handle TARGET_NOT_FOUND event - element hasn't loaded yet
    if (type === EVENTS.TARGET_NOT_FOUND) {
      // Set waiting state to show the indicator
      setIsWaitingForTarget(true);

      // Clear any existing timeout
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current);
      }

      // Simple approach: just wait and let the polling mechanism find it
      // The polling in the useEffect will detect when element appears
      // and clear the waiting state

      // After a timeout, if still waiting, check one more time then skip
      waitingTimeoutRef.current = setTimeout(() => {
        // Check one more time if element exists
        const targetElement =
          step?.target && document.querySelector(step.target);
        if (targetElement) {
          setIsWaitingForTarget(false);
          // Force Joyride to retry
          setRun(false);
          setTimeout(() => {
            setRun(true);
          }, 100);
        } else {
          console.warn(
            "[ProductTour] Element not found after waiting, skipping to next step"
          );
          setIsWaitingForTarget(false);
          if (stepIndex + 1 < steps.length) {
            changeStepIndex(stepIndex + 1);
            // Force Joyride to continue to next step
            setRun(false);
            setTimeout(() => {
              setRun(true);
            }, 100);
          } else {
            // No more steps, close tour
            resetTourState();
          }
        }
      }, 10000); // Wait up to 10 seconds

      return; // Don't process other events while waiting
    }

    // Clear waiting state when step renders successfully (means target was found)
    if (type === EVENTS.STEP_AFTER) {
      if (isWaitingForTarget) {
        setIsWaitingForTarget(false);
        setRetryCount(0);
      }

      // Mark story as completed when reaching the last step
      if (index === steps.length - 1 && selectedStory) {
        const isCompletedStory =
          isStoryCompleted(completedStories, selectedStory) ||
          dispatchedCompletionsRef.current.has(selectedStory);

        if (!isCompletedStory) {
          dispatchedCompletionsRef.current.add(selectedStory);

          // Update the story's completedCondition to "FINISHED"
          const updatedStories = stories.map((story) => {
            if (story.uuid === selectedStory) {
              return { ...story, completedCondition: "FINISHED" };
            }
            return story;
          });

          // Save updated stories to localStorage for persistence
          localStorage.setItem(
            "gettingStartedStoriesState",
            JSON.stringify(updatedStories)
          );

          // Dispatch action to update stories in the parent component
          if (window.updateGettingStartedStories) {
            window.updateGettingStartedStories(updatedStories);
          }

          if (isLoggedIn) {
            dispatch(updateCompletedStories(selectedStory));
          } else {
            dispatch(
              updateCompletedStoriesSuccess([
                ...completedStories,
                selectedStory,
              ])
            );
          }
        }
      }
    }

    step?.overlayStyles && handleProductTourStyling(step?.overlayStyles);
    if (step.target) {
      calculateSpotLightTop(step.target);
    }
    const next =
      type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT && !spotLightEvent;

    const prev = type === EVENTS.STEP_AFTER && action === ACTIONS.PREV;

    const stop = type === EVENTS.STEP_AFTER && action === ACTIONS.CLOSE;

    // Check if user clicked "Last" button (which is close on the last step)
    const isLastStep = index === steps.length - 1;

    if (next) {
      // Prevent advancing if we're at the last step
      if (stepIndex + 1 >= steps.length) {
        console.warn("[ProductTour] Cannot advance beyond last step");
        return;
      }

      if (step.navigate && step.navigationUrl) {
        history.push(step.navigationUrl);
        // Add small delay after navigation to let DOM update
        setTimeout(() => {
          changeStepIndex(stepIndex + 1);
        }, 200);
      } else {
        changeStepIndex(stepIndex + 1);
      }
      return;
    }

    if (prev) {
      if (stepIndex - 2 >= 0 && steps[stepIndex - 2].navigate) {
        history.push(steps[stepIndex - 2].navigationUrl);
      }

      return changeStepIndex(stepIndex - 1);
    }

    if (stop) {
      // Story completion is now handled when reaching the last step
      // Just reset the tour state when closing
      resetTourState();
    }
  };

  const selectStory = (id) => {
    // Complete reset before starting new story
    resetTourState();

    // Small delay to ensure clean state and Joyride unmount
    setTimeout(() => {
      setSelectedStory(id);
      const mapped = stories
        .find((s) => s.uuid === id)
        .steps.map((step) => {
          const { title, Title, ...rest } = step; // remove title to hide Joyride header
          const spotlightClicksValue = parseInt(step.spotlightClicks);
          return {
            ...rest,
            ...stepData,
            overlayStyles: step.styles,
            navigate: step.navigationUrl ? true : false,
            spotlightClicks: spotlightClicksValue,
            hideFooter: spotlightClicksValue === 1,
          };
        });
      setSteps(mapped);

      // If first step has a navigation URL and we're not there, navigate first
      const firstNav = mapped?.[0]?.navigationUrl;
      if (firstNav) {
        const isAbsolute = /^https?:\/\//i.test(firstNav);
        if (isAbsolute) {
          window.location.href = firstNav;
        } else {
          history.push(firstNav);
        }
      }

      // Delay tour start to ensure Joyride is fully initialized
      // This prevents the first "Next" button crash
      setTimeout(() => {
        setRun(true);
      }, 300);
    }, 100);
  };

  const evaluateStory = (story, index) => {
    const {
      uuid,
      disableForAnonymous,
      completedCondition,
      autoCompleteOnLogin,
    } = story;

    const completedStory =
      isStoryCompleted(completedStories, uuid) ||
      completedCondition === "FINISHED";
    const hasToBeLoggedIn = parseInt(disableForAnonymous);

    // If user is logged in, all stories should be enabled
    if (isLoggedIn) {
      return {
        ...story,
        disabled: false, // All stories enabled when logged in
        // Stories flagged autoCompleteOnLogin (e.g. Register) are satisfied
        // by the act of being logged in, with no persisted completion needed.
        completed: completedStory || !!autoCompleteOnLogin,
      };
    }

    // For anonymous users, disable stories that require login
    return {
      ...story,
      disabled: hasToBeLoggedIn,
      completed: completedStory,
    };
  };

  const toggleStoryExpansion = (storyId) => {
    // If clicking the same story, toggle it. Otherwise, expand the new one and close others
    setExpandedStoryId((prevId) => (prevId === storyId ? null : storyId));
  };

  return (
    <div className="product_tour_container">
      <ErrorBoundary>
        {/* Show loading indicator when waiting for target */}
        {isWaitingForTarget && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#f0f8ff",
              borderRadius: "4px",
              marginBottom: "10px",
              textAlign: "center",
              color: COLORS.DARK_BLUE,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  border: "2px solid #ddd",
                  borderTopColor: COLORS.DARK_BLUE,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></span>
              <span>Waiting for content to load...</span>
            </div>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        {stories.length > 0 &&
          stories.map((story, index) => {
            return (
              <ProductTourItem
                story={evaluateStory(story, index)}
                key={story.uuid}
                stepNumber={index + 1}
                stepIndex={stepIndex}
                selectStory={selectStory}
                selectedStory={selectedStory}
                isLoggedIn={isLoggedIn}
                completedStories={completedStories}
                isExpanded={expandedStoryId === story.uuid}
                toggleExpansion={toggleStoryExpansion}
              />
            );
          })}
        {selectedStory && steps && steps.length > 0 && (
          <Joyride
            key={tourKey} // Force remount when key changes
            scrollOffset={150}
            continuous
            showProgress
            run={run} // Keep running even when waiting so events can fire
            steps={steps}
            callback={handleJoyrideCallback}
            stepIndex={stepIndex < steps.length ? stepIndex : 0}
            styles={{
              options: {
                primaryColor: COLORS.DARK_BLUE,
                zIndex: 10000,
                overlayColor: "rgba(0, 0, 0, 0.5)",
              },
            }}
            disableScrolling={false}
            disableScrollParentFix={false}
          />
        )}
      </ErrorBoundary>
    </div>
  );
};

export default ProductTour;
